import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { nuclearn_api_url } from "src/App";

// Compute the base at call time, not module load — nuclearn_api_url is a
// let re-assigned during app bootstrap (same TDZ hazard the PG config
// service documents).
const base = () => `${nuclearn_api_url}/workflow/products`;

/**
 * Full agent config (system prompt included) by id. The by-id route does not
 * apply the worker-kind listing filter, so this is how the slots panel reads
 * a hidden seeded worker's prompt to prefill a customized copy.
 */
export const fetchAgentConfig = async (agentId: number) => {
  const response = await axios.get(
    `${nuclearn_api_url}/chat/config/id/${agentId}`,
  );
  return response.data;
};

export interface WorkflowSlotPersona {
  id: number;
  slug: string;
  name: string;
  description: string | null;
}

export interface WorkflowPersonaSlot {
  slot_key: string;
  description: string;
  default_persona_slug: string | null;
  resolved_persona: WorkflowSlotPersona | null;
  /** Which layer decided the persona: admin binding, legacy resource slot, or the seeded default. */
  resolution_source: "binding" | "resource_slot" | "default" | null;
  /** Which model this slot's launches run on, and which layer decided (backend SlotResponse.resolved_model). */
  resolved_model: {
    model_id: number;
    name: string;
    source:
      | "persona_override"
      | "persona_model_version"
      | "contract_default"
      | "platform_default";
  } | null;
  driven_stage_keys: string[];
}

export interface WorkflowStageDeclaration {
  stage_key: string;
  slot_key: string;
  advance: "auto" | "requires_approval";
  fan_out: "none" | "per_variant";
}

export interface WorkflowReferenceRole {
  role_key: string;
  display_name: string;
  required: boolean;
  description: string;
}

export interface WorkflowProductDeclaration {
  product_slug: string;
  display_name: string;
  license_module: string;
  persona_slots: WorkflowPersonaSlot[];
  stages: WorkflowStageDeclaration[];
  reference_roles: WorkflowReferenceRole[];
}

/**
 * Exported for non-hook callers (imperative flows like resuming a record
 * with the current lead agent); hook callers use
 * useWorkflowProductDeclaration for caching.
 */
export const getWorkflowProductDeclaration = async (
  slug: string,
): Promise<WorkflowProductDeclaration> => {
  const response = await axios.get(`${base()}/${slug}`);
  return response.data;
};

export const useWorkflowProductDeclaration = (slug: string | undefined) => {
  return useQuery(
    ["workflowProduct", slug],
    () => getWorkflowProductDeclaration(slug!),
    {
      enabled: !!slug,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
};

const resolveWorkflowPersona = async (
  slug: string,
  dialogueId?: number,
): Promise<{ persona: WorkflowSlotPersona | null }> => {
  const response = await axios.get(`${base()}/${slug}/resolve-persona`, {
    params: dialogueId != null ? { dialogue_id: dialogueId } : {},
  });
  return response.data;
};

/**
 * The persona the backend's product resolver would run for this context —
 * the same resolver the converse path uses when chat_config_id is null. With
 * a dialogueId this is per-dialogue truth (e.g. a PG stage's creation-time
 * persona); without, the product's fresh-chat resolution.
 */
export const useWorkflowResolvedPersona = (
  slug: string | undefined,
  dialogueId?: number,
) => {
  return useQuery(
    ["workflowResolvedPersona", slug ?? null, dialogueId ?? null],
    () => resolveWorkflowPersona(slug!, dialogueId),
    {
      enabled: !!slug,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
};

const bindSlotPersona = async (
  slug: string,
  slotKey: string,
  personaId: number,
): Promise<WorkflowPersonaSlot> => {
  const response = await axios.put(
    `${base()}/${slug}/persona-bindings/${slotKey}`,
    { persona_id: personaId },
  );
  return response.data;
};

const unbindSlotPersona = async (
  slug: string,
  slotKey: string,
): Promise<WorkflowPersonaSlot> => {
  const response = await axios.delete(
    `${base()}/${slug}/persona-bindings/${slotKey}`,
  );
  return response.data;
};

export const useBindSlotPersona = (slug: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ slotKey, personaId }: { slotKey: string; personaId: number }) =>
      bindSlotPersona(slug, slotKey, personaId),
    {
      onSuccess: () => queryClient.invalidateQueries(["workflowProduct", slug]),
    },
  );
};

export const useUnbindSlotPersona = (slug: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ slotKey }: { slotKey: string }) => unbindSlotPersona(slug, slotKey),
    {
      onSuccess: () => queryClient.invalidateQueries(["workflowProduct", slug]),
    },
  );
};
